import { z } from 'zod';
import { normalizeString } from 'utils/Validations';

export interface ShiftProfileGroupItemFormValues {
    shift_profile_id: number | null;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
}

export interface ShiftProfileGroupFormValues {
    id?: number | string;
    name: string;
    description?: string;
    items: ShiftProfileGroupItemFormValues[];
}

export const EMPTY_GROUP_ITEM: ShiftProfileGroupItemFormValues = {
    shift_profile_id: null,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
};

export function createShiftProfileGroupSchema(params: {
    t: (key: string, options?: any) => string;
    rows: any[];
    initialValues?: Partial<ShiftProfileGroupFormValues>;
    readOnly?: boolean;
}) {
    const { t, rows, initialValues, readOnly = false } = params;

    const itemSchema = z
        .object({
            shift_profile_id: z
                .number({ invalid_type_error: 'Seleccione un perfil' })
                .int()
                .positive('Seleccione un perfil'),
            monday: z.boolean(),
            tuesday: z.boolean(),
            wednesday: z.boolean(),
            thursday: z.boolean(),
            friday: z.boolean(),
            saturday: z.boolean(),
            sunday: z.boolean(),
        })
        .refine(
            (item) =>
                item.monday || item.tuesday || item.wednesday || item.thursday ||
                item.friday || item.saturday || item.sunday,
            { message: 'Seleccione al menos un día', path: ['monday'] },
        );

    return z
        .object({
            name: z
                .string()
                .trim()
                .min(3, t('general.zod.string.min_chars', { min: 3 }) as string)
                .max(100, t('general.zod.string.max_chars', { max: 100 }) as string),
            description: z
                .string()
                .trim()
                .max(255, t('general.zod.string.max_chars', { max: 255 }) as string)
                .optional()
                .or(z.literal('')),
            items: z.array(itemSchema).min(1, 'Agregue al menos un perfil'),
        })
        .superRefine((data, ctx) => {
            const normalizedInput = normalizeString(data.name);
            const currentId = initialValues?.id;
            const existsInRows = rows?.some((r: any) => {
                const sameName = normalizeString(r?.shift_profile_group_name ?? r?.name ?? '') === normalizedInput;
                const isSameRecord = currentId !== undefined && r?.id === currentId;
                return sameName && !isSameRecord;
            });
            if (!readOnly && existsInRows) {
                ctx.addIssue({
                    path: ['name'],
                    code: 'custom',
                    message: t('general.zod.string.name_exist', { defaultValue: 'El nombre ya existe' }) as string,
                });
            }
        });
}
